import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { API_ENDPOINTS } from 'Constants/env';
import { COMMUNICATOR_HIDE_CHAT_CALL_HISTORY } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import {
  IDesktopLocalPreferences,
  IPatchDataItem,
  IPreferenceButtonStates,
  IPreferences,
  IPreferencesWithUpdated,
} from 'Interfaces/preferences';
import { MobXProviderContext } from 'mobx-react';
import { useContext, useMemo } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { resolveShowCallMessagesInChat } from 'Utils/preferences/resolveShowCallMessagesInChat';
import API from '~/api';

type PreferencesUpdatesMap = {
  [key in keyof IPreferences]?: string;
};

export const QUERY_KEY = 'preferences';

export const defaultPreferences: IPreferencesWithUpdated = {
  notificationAudio: true,
  floatingSoftphone: false,
  ringVolumePercent: 1.0,
  updated: new Date().toISOString(),
  showCallMessagesInChat: true,
};

export const defaultButtonStates: IPreferenceButtonStates = {
  notificationAudio: true,
  floatingSoftphone: false,
  showCallMessagesInChat: true,
};

export const defaultDesktopLocalPreferences: IDesktopLocalPreferences = {
  openOnLogin: false,
};

export const fetchPreferences = async () => {
  const { data } = await API.get<
    IPreferencesWithUpdated & Partial<IDesktopLocalPreferences>
  >(API_ENDPOINTS.Preference);
  return data;
};

export const mutationOptions = (queryClient: QueryClient) => ({
  mutationFn: async (preferenceUpdates: IPreferences | null) => {
    const preferencePatchData: IPatchDataItem[] = Object.keys(
      preferenceUpdates
    ).map((key) => ({
      op: 'replace',
      path: `/${key}`,
      value: preferenceUpdates[key] as PreferencesUpdatesMap,
    }));

    await API.patch(API_ENDPOINTS.Preference, preferencePatchData);
    Object.keys(preferenceUpdates).forEach((key) => {
      pushToGTMDataLayer('preferenceSet', { preferenceName: key });
    });

    return preferenceUpdates;
  },
  onMutate: async (newPreferences: IPreferences | void) => {
    await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });

    const previousPreferences =
      queryClient.getQueryData<IPreferencesWithUpdated>([QUERY_KEY]);

    queryClient.setQueryData<IPreferencesWithUpdated>([QUERY_KEY], (old) => ({
      ...old,
      ...newPreferences,
    }));

    return { previousPreferences };
  },
  onError: (
    err,
    newPreferences,
    context: { previousPreferences?: IPreferencesWithUpdated }
  ) => {
    if (context?.previousPreferences) {
      queryClient.setQueryData([QUERY_KEY], context.previousPreferences);
    }
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  },
});

export const usePreferences = () => {
  const queryClient = useQueryClient();
  //get stores from context
  const { message, participant, contact, conversation } =
    useContext<RootStoreProps>(MobXProviderContext);

  const {
    [COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]: hideChatCallHistory,
  } = useFeatureFlags<boolean>([COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]);

  const preferencesQuery = useQuery<
    IPreferencesWithUpdated &
      IDesktopLocalPreferences &
      IPreferenceButtonStates,
    Error
  >({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const newPreferences = await fetchPreferences();
      // we set the query data with the settings from the cloud, and we append the local openOnLogin setting
      return queryClient.setQueryData<
        IPreferencesWithUpdated & Partial<IDesktopLocalPreferences>
      >([QUERY_KEY], (old) => ({
        ...newPreferences,
        openOnLogin:
          newPreferences?.openOnLogin ??
          old?.openOnLogin ??
          defaultDesktopLocalPreferences.openOnLogin,
      }));
    },
  });

  const updatePreferences = useMutation<IPreferences, Error, unknown>({
    ...mutationOptions(queryClient),
    onSuccess: () => {
      participant.clearAllData();
      message.clearAllData();
      contact.clearAllData();
      void conversation.reloadAndBackfillAllConversations();
    },
  });

  // Get desktop local preferences from preferencesQuery data
  const desktopLocalPreferences: IDesktopLocalPreferences =
    preferencesQuery?.data && {
      openOnLogin: preferencesQuery.data.openOnLogin,
    };

  const preferences = useMemo(() => {
    if (!preferencesQuery.data) {
      return preferencesQuery.data;
    }

    return {
      ...preferencesQuery.data,
      showCallMessagesInChat: resolveShowCallMessagesInChat(
        preferencesQuery.data.showCallMessagesInChat,
        hideChatCallHistory === true
      ),
    };
  }, [preferencesQuery.data, hideChatCallHistory]);

  // Get button states from effective preferences
  const buttonStates: IPreferenceButtonStates = preferences && {
    floatingSoftphone: preferences.floatingSoftphone,
    notificationAudio: preferences.notificationAudio,
    showCallMessagesInChat: preferences.showCallMessagesInChat,
  };

  const updateOpenOnLogin = async (openOnLogin: boolean) => {
    // since we using the react query as the only state management for the settings, we only need to append the local openOnLogin setting
    queryClient.setQueryData<IPreferencesWithUpdated>([QUERY_KEY], (old) => ({
      ...old,
      openOnLogin,
    }));
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }); // invalidate to force refetch and update the cached data
  };

  return {
    preferences,
    preferencesQuery,
    desktopLocalPreferences,
    buttonStates,
    updatePreferences,
    updateOpenOnLogin,
  };
};
